import type { DateGroup } from './dashboardStorage'
import { getZonedWeekKey } from './timeBucketing'
import { formatPivotValue, type PivotAggregation, type PivotCell, type PivotResult } from './pivot'

/**
 * Shared by NewDashboard.tsx (Panel, full editing) and Dashboard.tsx
 * (/home's pinned-widget preview, read-only) — pulled out of NewDashboard.tsx
 * so a widget renders identically in both places instead of via a second
 * duplicate implementation of the same chart/pivot-bucketing logic.
 */

export type ReportRunState = {
  loading: boolean
  error: string | null
  rows: Array<Record<string, unknown>>
}

export const toMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error))

export const formatNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '-'
  const abs = Math.abs(value)
  let digits = 2
  if (abs >= 1000) {
    digits = 0
  } else if (abs >= 100) {
    digits = 1
  }
  return value.toFixed(digits)
}

const escapeXml = (text: string): string => text
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')

const CHART_WIDTH = 720
const CHART_HEIGHT = 240
const CHART_PADDING = 36
const CHART_TOP_PADDING = 26
const CHART_LEFT_PADDING = 54
export const CHART_MAX_ITEMS = 12

// Axis/gridline/label chrome shared by buildBarChartSvg and
// buildLineChartSvg — the two chart types differ only in how they plot
// `trimmed` inside the same plot area.
const buildChartChrome = (input: {
  items: Array<{ label: string; value: number }>
  yAxisLabel: string
  trimFromEnd?: boolean
}): {
  trimmed: Array<{ label: string; value: number }>
  max: number
  chartW: number
  chartH: number
  gridLines: string
  axis: string
  yLabels: string
  yAxisTitle: string
} => {
  const { items, yAxisLabel, trimFromEnd } = input
  const trimmed = trimFromEnd
    ? items.slice(Math.max(0, items.length - CHART_MAX_ITEMS))
    : items.slice(0, CHART_MAX_ITEMS)
  const max = Math.max(0, ...trimmed.map((item) => item.value))
  const tickValues: number[] = [0, max / 2, max]

  const chartW = CHART_WIDTH - CHART_LEFT_PADDING - CHART_PADDING
  const chartH = CHART_HEIGHT - CHART_TOP_PADDING - CHART_PADDING

  const gridLines = tickValues.map((value) => {
    const ratio = max > 0 ? value / max : 0
    const y = CHART_TOP_PADDING + (chartH - ratio * chartH)
    return `<line x1="${CHART_LEFT_PADDING}" y1="${y.toFixed(2)}" x2="${(CHART_WIDTH - CHART_PADDING)}" y2="${y.toFixed(2)}" stroke="#e5e7eb" stroke-width="1" />`
  }).join('')

  const yLabels = tickValues.map((value) => {
    const ratio = max > 0 ? value / max : 0
    const y = CHART_TOP_PADDING + (chartH - ratio * chartH)
    return `<text x="${(CHART_LEFT_PADDING - 8).toFixed(2)}" y="${(y + 3).toFixed(2)}" text-anchor="end" font-size="10" fill="#475569">${escapeXml(formatNumber(value))}</text>`
  }).join('')

  const axis = [
    `<line x1="${CHART_LEFT_PADDING}" y1="${CHART_TOP_PADDING}" x2="${CHART_LEFT_PADDING}" y2="${CHART_HEIGHT - CHART_PADDING}" stroke="#9ca3af" stroke-width="1" />`,
    `<line x1="${CHART_LEFT_PADDING}" y1="${CHART_HEIGHT - CHART_PADDING}" x2="${CHART_WIDTH - CHART_PADDING}" y2="${CHART_HEIGHT - CHART_PADDING}" stroke="#9ca3af" stroke-width="1" />`
  ].join('')

  const yAxisTitle = yAxisLabel
    ? `<text x="14" y="${(CHART_TOP_PADDING + chartH / 2).toFixed(2)}" text-anchor="middle" font-size="10" fill="#475569" transform="rotate(-90 14 ${(CHART_TOP_PADDING + chartH / 2).toFixed(2)})">${escapeXml(yAxisLabel)}</text>`
    : ''

  return { trimmed, max, chartW, chartH, gridLines, axis, yLabels, yAxisTitle }
}

const truncateChartLabel = (label: string): string => (
  label.length > 14 ? `${label.slice(0, 14)}…` : label
)

export const buildBarChartSvg = (input: {
  items: Array<{ label: string; value: number }>
  yAxisLabel: string
  ariaLabel: string
  trimFromEnd?: boolean
}): string => {
  const { yAxisLabel, ariaLabel, trimFromEnd } = input
  const { trimmed, max, chartW, chartH, gridLines, axis, yLabels, yAxisTitle } = buildChartChrome({
    items: input.items,
    yAxisLabel,
    trimFromEnd
  })

  const barGap = 8
  const barW = trimmed.length > 0
    ? (chartW - barGap * (trimmed.length - 1)) / trimmed.length
    : chartW
  const bars = trimmed.map((item, index) => {
    const ratio = max > 0 ? item.value / max : 0
    const h = Math.max(0, Math.min(chartH, ratio * chartH))
    const x = CHART_LEFT_PADDING + index * (barW + barGap)
    const y = CHART_TOP_PADDING + (chartH - h)
    const labelX = x + barW / 2

    return [
      `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${barW.toFixed(2)}" height="${h.toFixed(2)}" fill="#60a5fa" />`,
      `<text x="${labelX.toFixed(2)}" y="${(CHART_HEIGHT - 10).toFixed(2)}" text-anchor="middle" font-size="10" fill="#374151">${escapeXml(truncateChartLabel(item.label))}</text>`
    ].join('')
  }).join('')

  return `<svg viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}" width="100%" height="${CHART_HEIGHT}" role="img" aria-label="${escapeXml(ariaLabel)}">${gridLines}${axis}${yLabels}${yAxisTitle}${bars}</svg>`
}

export const buildLineChartSvg = (input: {
  items: Array<{ label: string; value: number }>
  yAxisLabel: string
  ariaLabel: string
  trimFromEnd?: boolean
}): string => {
  const { yAxisLabel, ariaLabel, trimFromEnd } = input
  const { trimmed, max, chartW, chartH, gridLines, axis, yLabels, yAxisTitle } = buildChartChrome({
    items: input.items,
    yAxisLabel,
    trimFromEnd
  })

  const stepX = trimmed.length > 1 ? chartW / (trimmed.length - 1) : 0
  const points = trimmed.map((item, index) => {
    const ratio = max > 0 ? item.value / max : 0
    const x = CHART_LEFT_PADDING + (trimmed.length > 1 ? index * stepX : chartW / 2)
    const y = CHART_TOP_PADDING + (chartH - Math.max(0, Math.min(chartH, ratio * chartH)))
    return { x, y, item }
  })

  const polyline = points.length > 0
    ? `<polyline points="${points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')}" fill="none" stroke="#60a5fa" stroke-width="2" />`
    : ''

  const markersAndLabels = points.map(({ x, y, item }) => [
    `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="3" fill="#2563eb"><title>${escapeXml(`${item.label}: ${formatNumber(item.value)}`)}</title></circle>`,
    `<text x="${x.toFixed(2)}" y="${(CHART_HEIGHT - 10).toFixed(2)}" text-anchor="middle" font-size="10" fill="#374151">${escapeXml(truncateChartLabel(item.label))}</text>`
  ].join('')).join('')

  return `<svg viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}" width="100%" height="${CHART_HEIGHT}" role="img" aria-label="${escapeXml(ariaLabel)}">${gridLines}${axis}${yLabels}${yAxisTitle}${polyline}${markersAndLabels}</svg>`
}

// v2.2 findings.md #47: one line / bar per column key (e.g. per lot), so
// lots of very different sizes aren't collapsed into one averaged series.
// Fixed-order categorical palette (validated for CVD separation on the
// light chart surface); hues are assigned by series position in the
// already-sorted column keys, never cycled — past `CHART_MAX_SERIES` the
// caller shows a "showing N of M" note instead of inventing a 9th hue.
export const CHART_SERIES_COLORS = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948'
] as const
export const CHART_MAX_SERIES = CHART_SERIES_COLORS.length

export type ChartSeries = {
  name: string
  // One entry per x label; NaN = no data (a gap in a line, no bar).
  values: number[]
}

export const buildMultiSeriesChartSvg = (input: {
  xLabels: string[]
  series: ChartSeries[]
  chartType: 'bar' | 'line'
  yAxisLabel: string
  ariaLabel: string
  trimFromEnd?: boolean
}): string => {
  const { yAxisLabel, ariaLabel, trimFromEnd, chartType } = input
  const start = trimFromEnd ? Math.max(0, input.xLabels.length - CHART_MAX_ITEMS) : 0
  const end = trimFromEnd ? input.xLabels.length : Math.min(input.xLabels.length, CHART_MAX_ITEMS)
  const xLabels = input.xLabels.slice(start, end)
  const series = input.series.slice(0, CHART_MAX_SERIES).map((s) => ({ ...s, values: s.values.slice(start, end) }))

  // The shared chrome only needs each x position's max to scale the y axis.
  const { max, chartW, chartH, gridLines, axis, yLabels, yAxisTitle } = buildChartChrome({
    items: xLabels.map((label, i) => ({
      label,
      value: Math.max(0, ...series.map((s) => s.values[i]).filter((v) => Number.isFinite(v)))
    })),
    yAxisLabel
  })

  const toY = (value: number): number => {
    const ratio = max > 0 ? value / max : 0
    return CHART_TOP_PADDING + (chartH - Math.max(0, Math.min(chartH, ratio * chartH)))
  }

  const xAxisLabels = (centerOf: (index: number) => number): string => xLabels.map((label, index) => (
    `<text x="${centerOf(index).toFixed(2)}" y="${(CHART_HEIGHT - 10).toFixed(2)}" text-anchor="middle" font-size="10" fill="#374151">${escapeXml(truncateChartLabel(label))}</text>`
  )).join('')

  const tooltip = (name: string, label: string, value: number): string => (
    `<title>${escapeXml(`${name} · ${label}: ${formatNumber(value)}`)}</title>`
  )

  let marks = ''
  let labels = ''
  if (chartType === 'line') {
    const stepX = xLabels.length > 1 ? chartW / (xLabels.length - 1) : 0
    const xOf = (index: number): number => CHART_LEFT_PADDING + (xLabels.length > 1 ? index * stepX : chartW / 2)
    marks = series.map((s, seriesIndex) => {
      const color = CHART_SERIES_COLORS[seriesIndex]
      // Split into contiguous runs so a missing bucket is a visible gap,
      // not a line drawn straight through it.
      const runs: string[][] = []
      let current: string[] = []
      s.values.forEach((value, index) => {
        if (Number.isFinite(value)) {
          current.push(`${xOf(index).toFixed(2)},${toY(value).toFixed(2)}`)
        } else if (current.length > 0) {
          runs.push(current)
          current = []
        }
      })
      if (current.length > 0) runs.push(current)
      const lines = runs
        .filter((run) => run.length > 1)
        .map((run) => `<polyline points="${run.join(' ')}" fill="none" stroke="${color}" stroke-width="2" />`)
        .join('')
      const dots = s.values.map((value, index) => (
        Number.isFinite(value)
          ? `<circle cx="${xOf(index).toFixed(2)}" cy="${toY(value).toFixed(2)}" r="4" fill="${color}" stroke="#ffffff" stroke-width="2">${tooltip(s.name, xLabels[index], value)}</circle>`
          : ''
      )).join('')
      return lines + dots
    }).join('')
    labels = xAxisLabels(xOf)
  } else {
    const groupGap = 10
    const groupW = xLabels.length > 0 ? (chartW - groupGap * (xLabels.length - 1)) / xLabels.length : chartW
    const barGap = 2
    const barW = series.length > 0 ? Math.max(1, (groupW - barGap * (series.length - 1)) / series.length) : groupW
    const groupX = (index: number): number => CHART_LEFT_PADDING + index * (groupW + groupGap)
    marks = xLabels.map((label, index) => series.map((s, seriesIndex) => {
      const value = s.values[index]
      if (!Number.isFinite(value)) return ''
      const y = toY(value)
      const h = CHART_TOP_PADDING + chartH - y
      const x = groupX(index) + seriesIndex * (barW + barGap)
      return `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${barW.toFixed(2)}" height="${h.toFixed(2)}" fill="${CHART_SERIES_COLORS[seriesIndex]}">${tooltip(s.name, label, value)}</rect>`
    }).join('')).join('')
    labels = xAxisLabels((index) => groupX(index) + groupW / 2)
  }

  return `<svg viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}" width="100%" height="${CHART_HEIGHT}" role="img" aria-label="${escapeXml(ariaLabel)}">${gridLines}${axis}${yLabels}${yAxisTitle}${marks}${labels}</svg>`
}

export const isDateLikeFieldKey = (key: string): boolean => {
  const lower = key.toLowerCase()
  if (lower.includes('date')) return true
  if (lower.endsWith('at')) return true
  if (lower.includes('recorded')) return true
  return false
}

export const parseDateGroup = (value: unknown): '' | DateGroup => {
  const text = String(value)
  if (text === 'day' || text === 'week' || text === 'month' || text === 'quarter' || text === 'year') return text
  return ''
}

const bucketKeyToSortValue = (key: string): number | null => {
  const weekMatch = /^\s*(\d{4})-W(\d{2})\s*$/.exec(key)
  if (weekMatch) {
    const year = Number(weekMatch[1])
    const week = Number(weekMatch[2])
    if (Number.isFinite(year) && Number.isFinite(week)) return year * 53 + (week - 1)
  }

  const monthMatch = /^\s*(\d{4})-(\d{2})\s*$/.exec(key)
  if (monthMatch) {
    const year = Number(monthMatch[1])
    const month = Number(monthMatch[2])
    if (Number.isFinite(year) && Number.isFinite(month)) return year * 12 + (month - 1)
  }

  const quarterMatch = /^\s*(\d{4})-Q([1-4])\s*$/.exec(key)
  if (quarterMatch) {
    const year = Number(quarterMatch[1])
    const quarter = Number(quarterMatch[2])
    if (Number.isFinite(year) && Number.isFinite(quarter)) return year * 4 + (quarter - 1)
  }

  const yearMatch = /^\s*(\d{4})\s*$/.exec(key)
  if (yearMatch) {
    const year = Number(yearMatch[1])
    if (Number.isFinite(year)) return year
  }

  const parsed = Date.parse(key)
  if (!Number.isNaN(parsed)) return parsed
  return null
}

export const sortChartItemsChronologically = (items: Array<{ label: string; value: number }>): Array<{ label: string; value: number }> => {
  const scored = items.map((item, index) => ({
    item,
    index,
    score: bucketKeyToSortValue(item.label)
  }))

  const anyScored = scored.some((s) => s.score !== null)
  if (!anyScored) return items

  return scored
    .sort((a, b) => {
      if (a.score === null && b.score === null) return a.index - b.index
      if (a.score === null) return 1
      if (b.score === null) return -1
      return a.score - b.score
    })
    .map((s) => s.item)
}

const toDateBucketLabel = (value: unknown, group: DateGroup): string => {
  if (value === null || value === undefined) return ''
  let text = ''
  if (typeof value === 'string') {
    text = value.trim()
  } else if (typeof value === 'number' || typeof value === 'boolean') {
    text = String(value)
  }
  if (!text) return ''

  // A plain `YYYY-MM-DD` (or ISO datetime) row date has already been
  // resolved to the tenant's calendar day by the dataset — slice it rather
  // than round-tripping through `Date`, which could shift an ISO datetime
  // across midnight.
  if (group === 'day' && /^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10)

  const parsed = new Date(text)
  if (Number.isNaN(parsed.getTime())) return text

  const year = parsed.getUTCFullYear()
  const monthIndex = parsed.getUTCMonth()
  const month = String(monthIndex + 1).padStart(2, '0')

  if (group === 'day') return `${year}-${month}-${String(parsed.getUTCDate()).padStart(2, '0')}`
  if (group === 'year') return String(year)
  if (group === 'quarter') {
    const q = Math.floor(monthIndex / 3) + 1
    return `${year}-Q${q}`
  }
  if (group === 'week') {
    // The row's date has already been resolved to a tenant-zoned calendar
    // date by the dataset that emitted it (same convention `month`/
    // `quarter`/`year` above already rely on, reading UTC getters off
    // that pre-resolved string) — bucket with 'UTC' here rather than
    // introducing a new farm-timezone dependency into this generic,
    // per-row function.
    return getZonedWeekKey(text, 'UTC') ?? `${year}-${month}`
  }
  return `${year}-${month}`
}

// v2.2 findings.md #46: bucketing overwrites `rowField` with its bucket
// label, but `last` / `sumOfLast` still need the row's real date to know
// which row in a bucket is the latest — so the pre-bucket value is kept
// under this key.
export const PIVOT_RAW_ROW_VALUE_KEY = '__pivotRawRowValue'

// Which field decides "latest" for `last` / `sumOfLast`: the widget's own
// `orderField` when set (redirected to the preserved raw value if it's the
// bucketed row field); else, for a date-bucketed row field, that field's
// raw date; else none (input order).
export const resolvePivotOrderField = (widget: {
  rowField: string
  rowDateGroup?: DateGroup
  orderField?: string
}): string | undefined => {
  const explicit = widget.orderField?.trim()
  if (explicit) {
    return explicit === widget.rowField && widget.rowDateGroup ? PIVOT_RAW_ROW_VALUE_KEY : explicit
  }
  return widget.rowDateGroup ? PIVOT_RAW_ROW_VALUE_KEY : undefined
}

export const bucketRowsForWidget = (
  rows: Array<Record<string, unknown>>,
  widget: {
    rowField: string
    rowDateGroup?: DateGroup
    columnField?: string
    columnDateGroup?: DateGroup
  }
): Array<Record<string, unknown>> => {
  const rowGroup = widget.rowDateGroup
  const colGroup = widget.columnDateGroup
  const shouldBucketRow = Boolean(rowGroup)
  const shouldBucketCol = Boolean(colGroup && widget.columnField)
  if (!shouldBucketRow && !shouldBucketCol) return rows

  return rows.map((row) => {
    const next: Record<string, unknown> = { ...row }
    if (shouldBucketRow && rowGroup) {
      next[PIVOT_RAW_ROW_VALUE_KEY] = row[widget.rowField]
      next[widget.rowField] = toDateBucketLabel(row[widget.rowField], rowGroup)
    }
    if (shouldBucketCol && colGroup && widget.columnField) {
      next[widget.columnField] = toDateBucketLabel(row[widget.columnField], colGroup)
    }
    return next
  })
}

/**
 * v2.2 findings.md #59: a widget's table listed every bucket the report's
 * rows touched — e.g. ~30 weeks of "-" before the first SGR value, and a
 * column per old lot that is "-" in every row. For display only, drop the
 * rows and columns whose every cell is empty (NaN — no value to aggregate).
 * `count`/`sum` never produce NaN, so their real zeros are always kept.
 * Totals are untouched (an all-empty row/column contributes nothing).
 */
export const trimEmptyPivotForDisplay = (pivot: PivotResult, aggregation: PivotAggregation): PivotResult => {
  const isEmpty = (cell: PivotCell): boolean => !Number.isFinite(formatPivotValue(cell, aggregation))
  const hasColumns = pivot.columnKeys.length > 0
  const keptColumns = hasColumns
    ? pivot.columnKeys.map((_, columnIndex) => columnIndex).filter((columnIndex) => (
      pivot.cells.some((row) => !isEmpty(row[columnIndex]))
    ))
    : pivot.columnKeys.map((_, columnIndex) => columnIndex)
  const keptRows = pivot.rowKeys.map((_, rowIndex) => rowIndex).filter((rowIndex) => (
    hasColumns
      ? keptColumns.some((columnIndex) => !isEmpty(pivot.cells[rowIndex][columnIndex]))
      : !isEmpty(pivot.rowTotals[rowIndex])
  ))
  if (keptRows.length === pivot.rowKeys.length && keptColumns.length === pivot.columnKeys.length) return pivot
  return {
    ...pivot,
    rowKeys: keptRows.map((rowIndex) => pivot.rowKeys[rowIndex]),
    columnKeys: keptColumns.map((columnIndex) => pivot.columnKeys[columnIndex]),
    cells: keptRows.map((rowIndex) => (
      hasColumns ? keptColumns.map((columnIndex) => pivot.cells[rowIndex][columnIndex]) : pivot.cells[rowIndex]
    )),
    rowTotals: keptRows.map((rowIndex) => pivot.rowTotals[rowIndex]),
    columnTotals: keptColumns.map((columnIndex) => pivot.columnTotals[columnIndex])
  }
}
